import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { MessageSquare, Send, ArrowLeft, ArrowRight, Package, Users, Gift, CheckCircle, XCircle, ShieldAlert, Check, CheckCheck, Wifi, WifiOff, Paperclip, Search, FileText, Image, X, Download, DollarSign, CircleDollarSign, ThumbsDown, HelpCircle, Clock, Eye, Upload, Bell, Star, AlertTriangle, LayoutGrid } from "lucide-react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import type { Message, Load, LoadItem } from "@shared/schema";
import { EmailVerificationModal } from "@/components/EmailVerificationModal";
import { useSocket } from "@/contexts/SocketContext";
import { useUpload } from "@/hooks/use-upload";
import MemberLink from "@/components/MemberLink";
import LoadShareCard from "@/components/LoadShareCard";
import LoadDetailModal from "@/components/LoadDetailModal";
import RatingModal from "@/components/RatingModal";
import ShipmentLoadDetails from "@/components/ShipmentLoadDetails";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return debouncedValue;
}

interface ConversationPartner {
  partnerId: string;
  latestMessage: Message;
  unreadCount: number;
  partner: {
    id: string;
    firstName: string;
    lastName: string;
    company: string | null;
    companyLogo: string | null;
    userType: string;
  };
}

// Load-based thread interface
interface LoadThread {
  partnerId: string;
  loadId: string | null;
  latestMessage: Message;
  unreadCount: number;
  partner: {
    id: string;
    firstName: string;
    lastName: string;
    company: string | null;
    companyLogo: string | null;
    userType: string;
  };
  load: {
    id: string;
    loadIdentifier: string | null;
    origin: string;
    destination: string;
    status: string;
    createdAt: Date;
    userId: string;
    pickupDate?: Date | string | null;
    deliveryDate?: Date | string | null;
    weight?: number | null;
    equipmentType?: string | null;
    liftgatePickup?: boolean | null;
    insidePickup?: boolean | null;
    liftgateDelivery?: boolean | null;
    insideDelivery?: boolean | null;
    deliveryAppointmentRequired?: boolean | null;
    callBeforeDelivery?: boolean | null;
    details?: string | null;
    pickupLocationType?: string | null;
    deliveryLocationType?: string | null;
    stackable?: boolean | null;
    hazardous?: boolean | null;
    pickupTimeWindow?: string | null;
    appointmentContactName?: string | null;
    appointmentContactPhone?: string | null;
    pickupTimeStart?: string | null;
    pickupTimeEnd?: string | null;
    deliveryTimeStart?: string | null;
    deliveryTimeEnd?: string | null;
    pickupAddress?: string | null;
    deliveryAddress?: string | null;
    pickupContactName?: string | null;
    pickupContactPhone?: string | null;
    deliveryContactName?: string | null;
    deliveryContactPhone?: string | null;
    dimensions?: string | null;
    handlingUnit?: string | null;
    length?: number | null;
    width?: number | null;
    height?: number | null;
    quantity?: number | null;
    bolUploaded?: boolean | null;
    bolUploadedAt?: Date | string | null;
    completedAt?: Date | string | null;
    items?: LoadItem[];
  } | null;
  threadPhase: string | null;
  threadId: string | null;
}

function MessageBubble({ 
  message, 
  isOwn, 
  onAcceptClaim,
  isAcceptingClaim,
  onAcceptQuote,
  onDeclineQuote,
  isAcceptingQuote,
  isDecliningQuote,
  shipperCompany,
  quoteAmount,
  pickupTimeWindow,
  isRevision,
}: { 
  message: Message; 
  isOwn: boolean;
  onAcceptClaim?: (messageId: string) => void;
  isAcceptingClaim?: boolean;
  onAcceptQuote?: (messageId: string) => void;
  onDeclineQuote?: (messageId: string) => void;
  isAcceptingQuote?: boolean;
  isDecliningQuote?: boolean;
  shipperCompany?: string | null;
  quoteAmount?: string | null;
  pickupTimeWindow?: string | null;
  isRevision?: boolean;
}) {
  const formatTime = (dateString: Date) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", { 
      hour: "numeric", 
      minute: "2-digit" 
    });
  };

  const isClaimInvitation = message.messageType === "claim_invitation";
  const isPending = message.claimInvitationStatus === "pending";
  const isAccepted = message.claimInvitationStatus === "accepted";
  const isRead = !!message.readAt;
  const [attachmentDialog, setAttachmentDialog] = useState<{ url: string; name: string; type: string } | null>(null);

  const isPreQuoteAnnouncement = message.message?.includes('Pre-Quote Questions') && 
    message.message?.includes('question about this load');

  if (isPreQuoteAnnouncement) {
    const bodyText = message.message
      .replace(/📋\s*/, '')
      .replace(/Pre-Quote Questions\s*\n*/, '')
      .trim();

    return (
      <div className="flex justify-center mb-4">
        <div className="max-w-xl w-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-700 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-md bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <span className="font-semibold text-foreground">Pre-Quote Questions</span>
          </div>
          <p className="text-base md:text-sm text-foreground/80" data-testid={`text-message-${message.id}`}>
            {bodyText || message.message}
          </p>
          <p className="text-xs text-amber-600/70 dark:text-amber-400/70 mt-3" data-testid={`text-time-${message.id}`}>
            {formatTime(message.createdAt)}
          </p>
        </div>
      </div>
    );
  }

  if (isClaimInvitation) {
    // Shipper side: inline system event pill (no card)
    if (isOwn) {
      const pillText = isAccepted
        ? 'Quote accepted · carrier confirmed'
        : 'Quote accepted · waiting for carrier to confirm';
      return (
        <div className="flex flex-col items-center mb-4 gap-1" data-testid={`text-message-${message.id}`}>
          <div className="flex items-center w-full gap-3">
            <div className="flex-1 border-b border-border" />
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted">
              <svg className="w-3 h-3 text-muted-foreground flex-shrink-0" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 6.5L4.5 9L10 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="text-xs text-muted-foreground whitespace-nowrap">{pillText}</span>
            </div>
            <div className="flex-1 border-b border-border" />
          </div>
          <span className="text-xs text-muted-foreground">{formatTime(message.createdAt)}</span>
        </div>
      );
    }

    // Carrier side: left-aligned card with orange border and CTA
    const bodyText = shipperCompany && quoteAmount
      ? `${shipperCompany} accepted your quote of $${quoteAmount}. Confirm below to lock in this load.`
      : 'Your quote was accepted. Confirm below to lock in this load.';

    return (
      <div className="flex justify-start mb-4">
        <div className={`max-w-xl w-full border bg-card rounded-xl p-4 ${isPending ? 'border-orange-200 dark:border-orange-800' : 'border-border'}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="font-medium text-sm text-foreground">Your quote was accepted</span>
            {isAccepted ? (
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-500 flex-shrink-0">
                <CheckCircle className="w-3 h-3 mr-1" />
                Accepted
              </Badge>
            ) : (
              <span className="text-xs text-muted-foreground flex-shrink-0">{formatTime(message.createdAt)}</span>
            )}
          </div>

          <p className="text-base md:text-sm text-muted-foreground" data-testid={`text-message-${message.id}`}>{bodyText}</p>

        </div>
      </div>
    );
  }

  const isQuoteSubmission = message.messageType === "quote_submission";
  const isQuoteDeclined = message.claimInvitationStatus === "declined";
  
  // Parse quote amount from message for prominent display
  const parseQuoteAmount = (msg: string): { amount: string | null; formattedMessage: React.ReactNode } => {
    const quoteMatch = msg.match(/(?:My Quote:|Quote:)\s*\$?([\d,]+(?:\.\d{2})?)/i);
    const amount = quoteMatch ? quoteMatch[1] : null;
    
    // Split message into sections for better formatting
    const lines = msg.split(/\n|(?=•)|(?=Load Summary:)|(?=My Quote:)/g).filter(line => line.trim());
    
    return {
      amount,
      formattedMessage: (
        <div className="space-y-2">
          {lines.map((line, idx) => {
            const trimmed = line.trim();
            if (trimmed.startsWith('•')) {
              return (
                <div key={idx} className="text-base md:text-sm pl-2 text-muted-foreground">
                  {trimmed}
                </div>
              );
            } else if (trimmed.toLowerCase().includes('load summary')) {
              return (
                <div key={idx} className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-2">
                  Load Details
                </div>
              );
            } else if (trimmed.toLowerCase().startsWith('my quote:')) {
              return null; // Skip - we show this prominently above
            } else if (trimmed) {
              return (
                <div key={idx} className="text-base md:text-sm">
                  {trimmed}
                </div>
              );
            }
            return null;
          })}
        </div>
      )
    };
  };

  if (isQuoteSubmission) {
    // Use quotePrice (integer cents) directly — no regex parsing needed.
    const displayAmount = message.quotePrice && message.quotePrice > 0
      ? `$${(message.quotePrice / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
      : '';

    return (
      <div className="flex flex-col items-start mb-4">
        <div className="max-w-[65%] bg-background border border-border rounded-xl rounded-bl-sm px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <CircleDollarSign className="w-4 h-4 text-orange-500 flex-shrink-0" />
            <span className="text-xs font-medium text-orange-500 uppercase tracking-wide">
              {isRevision ? 'Quote revised' : 'Quote submitted'}
            </span>
            {isAccepted && (
              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-500 text-xs">
                <CheckCircle className="w-3 h-3 mr-1" />Accepted
              </Badge>
            )}
            {isQuoteDeclined && (
              <Badge variant="outline" className="text-muted-foreground text-xs">Declined</Badge>
            )}
          </div>
          <span className="text-[18px] font-medium text-foreground">{displayAmount}</span>
          {message.message && (
            <p className="text-sm text-foreground/80 mt-2 whitespace-pre-line" data-testid={`text-message-${message.id}`}>
              {message.message}
            </p>
          )}
          <p className="text-xs text-muted-foreground mt-1">{formatTime(message.createdAt)}</p>
        </div>
      </div>
    );
  }

  const hasAttachment = message.attachmentUrl;
  const isImage = message.attachmentType?.startsWith('image/');

  const renderAttachment = () => {
    if (!hasAttachment) return null;

    const openDialog = () => setAttachmentDialog({
      url: message.attachmentUrl!,
      name: message.attachmentName || 'File',
      type: message.attachmentType || '',
    });
    
    if (isImage) {
      return (
        <div className="mb-2">
          <div
            className="cursor-pointer"
            onClick={openDialog}
            data-testid={`attachment-image-${message.id}`}
          >
            <img 
              src={message.attachmentUrl!} 
              alt={message.attachmentName || 'Image'} 
              className="max-w-full max-h-48 rounded-md hover:opacity-90 transition-opacity"
            />
          </div>
          {message.attachmentName && (
            <p className={`text-xs mt-1 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
              {message.attachmentName}
            </p>
          )}
        </div>
      );
    }
    
    return (
      <div
        onClick={openDialog}
        className={`flex items-center gap-2 mb-2 p-2 rounded cursor-pointer ${isOwn ? 'bg-primary-foreground/10' : 'bg-background/50'} hover:opacity-80 transition-opacity`}
        data-testid={`attachment-file-${message.id}`}
      >
        <FileText className="w-5 h-5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-base md:text-sm font-medium truncate">{message.attachmentName || 'File'}</p>
          {message.attachmentSize && (
            <p className={`text-xs ${isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground'}`}>
              {(message.attachmentSize / 1024).toFixed(1)} KB
            </p>
          )}
        </div>
        <Download className="w-4 h-4 flex-shrink-0" />
      </div>
    );
  };

  // Check if this is a Load Confirmed system message
  const isLoadConfirmed = message.message.includes('🚛 LOAD CONFIRMED');

  // Render Load Confirmed messages with redesigned card (Option B)
  if (isLoadConfirmed) {
    const msg = message.message;

    // Extract route from "Load: Chicago, IL → LA, CA (15,000 lbs)"
    const loadLineMatch = msg.match(/Load:\s*(.+)/);
    const loadLine = loadLineMatch?.[1] || '';
    const route = loadLine.replace(/\s*\([^)]+\)$/, '').trim();

    // Extract pickup date
    const pickupMatch = msg.match(/Pickup:\s*(.+)/);
    const pickupDate = pickupMatch?.[1]?.trim() || '';

    // isOwn=true means carrier sent this message
    const isCarrierView = isOwn;

    return (
      <div className="flex justify-start mb-4">
        <div className="max-w-xl w-full border border-border bg-card rounded-xl p-4">
          <div data-testid={`text-message-${message.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <span className="font-medium text-base md:text-sm">Shipment confirmed</span>
              </div>
              <Badge className="bg-green-500 text-white text-xs no-default-active-elevate">Active</Badge>
            </div>

            <div className="bg-secondary rounded-lg p-3 mb-2 space-y-1.5">
              {/* Pickup row — shipper: green action pill when null, value when set */}
              {!isCarrierView && (
                <div className="grid grid-cols-2 gap-1 items-center">
                  <span className="text-xs text-muted-foreground">Pickup</span>
                  {pickupTimeWindow ? (
                    <span className="text-xs font-medium text-right">{pickupTimeWindow}</span>
                  ) : (
                    <div className="flex justify-end">
                      <span className="text-xs font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950 rounded px-2 py-0.5">
                        Provide pickup time and details in the left panel.
                      </span>
                    </div>
                  )}
                </div>
              )}
              {/* Pickup row — carrier: quiet status when null, hidden when set */}
              {isCarrierView && !pickupTimeWindow && (
                <div className="grid grid-cols-2 gap-1 items-center">
                  <span className="text-xs text-muted-foreground">Pickup</span>
                  <span className="text-xs text-muted-foreground italic text-right">Waiting on shipper to provide pickup details.</span>
                </div>
              )}
              {route && (
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-xs text-muted-foreground">Route</span>
                  <span className="text-xs font-medium text-right">{route}</span>
                </div>
              )}
              {pickupDate && (
                <div className="grid grid-cols-2 gap-1">
                  <span className="text-xs text-muted-foreground">Pickup date</span>
                  <span className="text-xs font-medium text-right">{pickupDate}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end gap-1 mt-1">
            <span className="text-xs text-muted-foreground">{formatTime(message.createdAt)}</span>
            {isOwn && (
              <span data-testid={`read-receipt-${message.id}`} title={isRead ? 'Read' : 'Delivered'}>
                {isRead ? (
                  <CheckCheck className="w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                  <Check className="w-3.5 h-3.5 text-muted-foreground/50" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (message.messageType === 'bol_upload') {
    const bolLinkText = isOwn
      ? (message.attachmentName ?? 'Bill of Lading')
      : 'Bill of Lading';
    return (
      <div className="flex justify-start mb-4">
        <div className="max-w-xl w-full border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950 rounded-xl p-4" data-testid={`bol-upload-message-${message.id}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
            </div>
            <span className="text-sm font-medium text-green-700 dark:text-green-400">BOL uploaded · on record</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <FileText className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            {message.attachmentUrl ? (
              <a
                href={message.attachmentUrl + '?preview=1'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-orange-500 hover:underline cursor-pointer"
                data-testid={`bol-view-${message.id}`}
              >
                {bolLinkText}
              </a>
            ) : (
              <span className="text-sm text-foreground">{bolLinkText}</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-right">{formatTime(message.createdAt)}</p>
        </div>
      </div>
    );
  }

  const canPreview = attachmentDialog
    ? !['officedocument', 'msword', 'ms-excel'].some(t => attachmentDialog.type.includes(t))
    : false;

  const handlePreview = () => {
    if (!attachmentDialog) return;
    window.open(attachmentDialog.url + '?preview=1', '_blank');
    setAttachmentDialog(null);
  };

  const handleDownload = () => {
    if (!attachmentDialog) return;
    const a = document.createElement('a');
    a.href = attachmentDialog.url;
    a.download = attachmentDialog.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setAttachmentDialog(null);
  };

  return (
    <>
      <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} mb-4`}>
        <div className={`max-w-[65%] ${isOwn ? 'bg-primary text-primary-foreground rounded-xl rounded-br-sm' : 'bg-card border border-border rounded-xl rounded-bl-sm'} px-4 py-3`}>
          {renderAttachment()}
          {message.messageType !== 'attachment' && (
            <p className="text-base md:text-sm whitespace-pre-line" data-testid={`text-message-${message.id}`}>{message.message}</p>
          )}
          {message.messageType === 'attachment' && !hasAttachment && (
            <p className="text-base md:text-sm whitespace-pre-line" data-testid={`text-message-${message.id}`}>{message.message}</p>
          )}
        </div>
        <div className={`flex items-center gap-1 mt-1 px-1 ${isOwn ? 'justify-end' : 'justify-start'}`}>
          <span className="text-xs text-muted-foreground">
            {formatTime(message.createdAt)}
          </span>
          {isOwn && (
            <span data-testid={`read-receipt-${message.id}`} title={isRead ? 'Read' : 'Delivered'}>
              {isRead ? (
                <CheckCheck className="w-3.5 h-3.5 text-muted-foreground" />
              ) : (
                <Check className="w-3.5 h-3.5 text-muted-foreground/50" />
              )}
            </span>
          )}
        </div>
      </div>

      <Dialog open={!!attachmentDialog} onOpenChange={(open) => { if (!open) setAttachmentDialog(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="sr-only" data-testid="dialog-attachment-title">
              {attachmentDialog?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-3">
            <div className="bg-orange-50 dark:bg-orange-950/40 rounded-md p-2.5 flex-shrink-0">
              <FileText className="text-orange-500" style={{ width: 22, height: 22 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[15px] font-medium truncate">{attachmentDialog?.name}</p>
              <p className="text-[13px] text-muted-foreground">
                {attachmentDialog ? (() => {
                  const mime = attachmentDialog.type;
                  let label = mime.split('/').pop()?.toUpperCase() ?? 'FILE';
                  if (mime.startsWith('image/')) label = label + ' image';
                  else if (mime === 'application/pdf') label = 'PDF document';
                  else if (mime.includes('wordprocessingml') || mime.includes('msword')) label = 'Word document';
                  else if (mime.includes('spreadsheetml') || mime.includes('ms-excel')) label = 'Excel spreadsheet';
                  else if (mime === 'text/plain') label = 'Text file';
                  else if (mime === 'text/csv') label = 'CSV file';
                  return label;
                })() : ''}
              </p>
            </div>
          </div>
          <div className="border-t mt-4 pt-4">
            <p className="text-[15px] text-muted-foreground mb-4">How would you like to open this file?</p>
            <DialogFooter className="flex-row gap-2 sm:gap-2">
              {canPreview && (
                <Button variant="outline" className="flex-1" onClick={handlePreview} data-testid="button-attachment-preview">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
              )}
              <Button
                className="flex-1 bg-orange-500 text-white"
                onClick={handleDownload}
                data-testid="button-attachment-download"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// BOL Upload workflow card — shown to carriers when BOL has not yet been uploaded
function BolUploadCard({ loadId, onUploaded, initialBolUploaded, initialBolFileName, initialBolFileUrl }: { loadId: string; onUploaded: () => void; initialBolUploaded?: boolean; initialBolFileName?: string; initialBolFileUrl?: string }) {
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success'>(initialBolUploaded ? 'success' : 'idle');
  const [uploadedFile, setUploadedFile] = useState<{ url: string; name: string } | null>(
    initialBolUploaded ? { url: initialBolFileUrl || '', name: initialBolFileName || 'Bill of Lading' } : null
  );
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const bolFileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const BOL_ALLOWED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/heic',
    'image/heif',
  ];

  const { uploadFile, isUploading } = useUpload({
    allowedTypes: BOL_ALLOWED_TYPES,
    onError: (err) => {
      setUploadError(err.message);
      setUploadState('idle');
    },
  });

  const handleBolFile = async (file: File) => {
    setUploadError(null);
    setUploadState('uploading');

    const result = await uploadFile(file);
    if (!result) {
      setUploadState('idle');
      return;
    }

    try {
      await apiRequest('POST', `/api/loads/${loadId}/bol-upload`, {
        attachmentUrl: result.objectPath,
        attachmentName: result.metadata.name,
        attachmentType: result.metadata.contentType,
        attachmentSize: result.metadata.size,
      });

      setUploadedFile({ url: result.objectPath, name: result.metadata.name });
      setUploadState('success');

      // Optimistically mark bolUploaded in the threads cache so the progress
      // tracker advances immediately without waiting for the async refetch.
      queryClient.setQueryData<LoadThread[]>(['/api/messages/threads'], (old) => {
        if (!old) return old;
        return old.map(t =>
          t.loadId === loadId && t.load
            ? { ...t, load: { ...t.load, bolUploaded: true } }
            : t
        );
      });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      onUploaded();
    } catch (err: any) {
      setUploadState('idle');
      const msg = err?.message || 'Failed to record BOL upload';
      setUploadError(msg);
      toast({ title: 'BOL upload failed', description: msg, variant: 'destructive' });
    }
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleBolFile(file);
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleBolFile(file);
  };

  if (uploadState === 'success' && uploadedFile) {
    return (
      <div className="mx-6 mb-3 border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/30 rounded-xl p-4 flex-shrink-0" data-testid="bol-upload-success-card">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
          </div>
          <span className="text-sm font-medium text-green-700 dark:text-green-300">BOL uploaded · on record</span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-foreground truncate">{uploadedFile.name}</span>
          {uploadedFile.url && (
            <button
              onClick={() => window.open(uploadedFile.url + '?preview=1', '_blank')}
              className="text-xs text-primary underline flex-shrink-0 ml-auto"
              data-testid="bol-success-view"
            >
              View
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-6 mb-3 border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20 rounded-xl p-4 flex-shrink-0" data-testid="bol-upload-card">
      <div className="flex items-center gap-2 mb-1">
        <Bell className="w-4 h-4 text-orange-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wide">Your turn · Carrier</span>
      </div>
      <p className="text-base md:text-sm text-foreground mb-3">Upload your Bill of Lading before pickup. Both parties will have it on record.</p>

      <div
        className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${isDragOver ? 'border-orange-400 bg-orange-50 dark:bg-orange-950/40' : 'border-border hover:border-orange-300 dark:hover:border-orange-600'}`}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={onDrop}
        onClick={() => bolFileInputRef.current?.click()}
        data-testid="bol-upload-zone"
      >
        <input
          ref={bolFileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.heic,.heif"
          className="hidden"
          onChange={onFileChange}
          data-testid="bol-file-input"
        />
        {isUploading || uploadState === 'uploading' ? (
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-muted-foreground">Uploading...</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="w-6 h-6 text-muted-foreground" />
            <span className="text-base md:text-sm text-muted-foreground">Drag and drop, or <span className="text-primary underline">browse</span></span>
            <span className="text-xs text-muted-foreground">PDF, JPG, PNG, HEIC accepted · max 10 MB</span>
          </div>
        )}
      </div>

      {uploadError && (
        <p className="text-xs text-destructive mt-2" data-testid="bol-upload-error">{uploadError}</p>
      )}
    </div>
  );
}

// Thread item component for load-based conversations
function ThreadItem({ 
  thread, 
  isSelected, 
  onClick,
  onLoadClick 
}: { 
  thread: LoadThread; 
  isSelected: boolean;
  onClick: () => void;
  onLoadClick?: (loadId: string) => void;
}) {
  const getInitials = () => {
    const first = thread.partner.firstName?.[0] || "";
    const last = thread.partner.lastName?.[0] || "";
    return (first + last).toUpperCase() || "U";
  };

  const formatTime = (dateString: Date) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    } else if (diffDays < 7) {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    } else {
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    }
  };

  const getStatusBadge = () => {
    const phase = thread.threadPhase;
    const loadStatus = thread.load?.status;

    if (!thread.load) {
      return (
        <Badge variant="secondary" className="text-xs px-1.5 py-0">
          General
        </Badge>
      );
    }
    if (phase === null || phase === 'in_progress') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
          Active
        </span>
      );
    }
    if (phase === 'pre_quote_question') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
          Quote pending
        </span>
      );
    }
    if (phase === 'quote_submitted') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
          Quote pending
        </span>
      );
    }
    if (phase === 'confirmed') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
          Confirmed
        </span>
      );
    }
    if (phase === 'completed') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300">
          Rating Pending
        </span>
      );
    }
    if (phase === 'closed') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
          Closed
        </span>
      );
    }
    if (phase === 'quote_declined' || phase === 'auto_closed' || loadStatus === 'Closed') {
      return (
        <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-muted text-muted-foreground">
          Closed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-full px-1.5 py-0 text-xs bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300">
        Active
      </span>
    );
  };

  const hasUnread = thread.unreadCount > 0;
  const loadIdentifier = thread.load?.loadIdentifier || null;

  return (
    <button
      onClick={onClick}
      className={`w-full p-3 flex items-start gap-3 text-left hover-elevate rounded-md transition-colors ${
        isSelected ? 'bg-primary/10 border border-orange-500' : 'border border-transparent'
      } ${hasUnread && !isSelected ? 'bg-primary/5' : ''}`}
      data-testid={`thread-${thread.partnerId}-${thread.loadId || 'general'}`}
    >
      <div className="relative">
        <Avatar className="w-11 h-11 flex-shrink-0">
          {thread.partner.companyLogo && (
            <AvatarImage src={thread.partner.companyLogo.startsWith('/objects/') ? thread.partner.companyLogo : `/objects/${thread.partner.companyLogo}`} alt={thread.partner.firstName} className="object-cover" />
          )}
          <AvatarFallback className={`text-sm text-white ${isSelected ? 'bg-orange-500' : 'bg-gray-400'}`}>{getInitials()}</AvatarFallback>
        </Avatar>
        {hasUnread && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center"
            data-testid={`unread-badge-${thread.partnerId}`}
          >
            {thread.unreadCount > 9 ? '9+' : thread.unreadCount}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        {/* Row 1: Name + Load ID at top right (replaces timestamp) */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className={`text-[15px] truncate ${hasUnread ? 'font-bold' : 'font-medium'}`}>
            {thread.partner.firstName} {thread.partner.lastName}
          </span>
          {loadIdentifier ? (
            <span
              className="font-mono text-xs text-muted-foreground underline underline-offset-2 cursor-pointer flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                const id = thread.load?.id || thread.loadId;
                if (id && onLoadClick) onLoadClick(id);
              }}
              data-testid={`badge-load-link-${thread.loadId || 'unknown'}`}
            >
              {loadIdentifier}
            </span>
          ) : null}
        </div>

        {/* Row 2: Status pill only, left aligned */}
        <div className="mb-1">
          {getStatusBadge()}
        </div>

        {/* Row 3: Latest message preview */}
        <p className={`text-base md:text-sm truncate ${hasUnread ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
          {thread.latestMessage.message}
        </p>
      </div>
    </button>
  );
}

const SHIPMENT_STEPS = [
  "Load posted",
  "Carrier contacted",
  "Quote accepted",
  "Pickup details confirmed",
  "BOL on record",
  "Delivery confirmed",
  "Rate experience",
];

function getActiveStep(thread: LoadThread | null): number {
  if (!thread) return 2;
  const phase = thread.threadPhase;
  if (!phase || phase === 'pre_quote_question') return 1;
  if (phase === 'quote_submitted') return 2;
  if (phase === 'active_shipment') {
    if (thread.load?.bolUploaded) return 6;
    return 5;
  }
  if (phase === 'completed') return 7;
  return 2;
}

interface ShipmentDetailsPanelProps {
  selectedThread: LoadThread | null;
  messages: Message[] | undefined;
  user: any;
  handleAcceptQuote: (messageId: string) => void;
  handleDeclineQuote: (messageId: string) => void;
  acceptQuoteMutation: { isPending: boolean };
  declineQuoteMutation: { isPending: boolean };
  onCompareBids: () => void;
  submitQuoteFromThreadMutation: {
    isPending: boolean;
    mutate: (data: { price: string; message?: string }) => void;
  };
  declineToQuoteMutation: {
    isPending: boolean;
    mutate: (data: { reason: string }) => void;
  };
  toast: ReturnType<typeof useToast>['toast'];
  loadAlreadyBooked?: boolean;
}

function ShipmentDetailsPanel({
  selectedThread,
  messages,
  user,
  handleAcceptQuote,
  handleDeclineQuote,
  acceptQuoteMutation,
  declineQuoteMutation,
  onCompareBids,
  submitQuoteFromThreadMutation,
  declineToQuoteMutation,
  toast,
  loadAlreadyBooked,
}: ShipmentDetailsPanelProps) {
  const [showSubmitQuote, setShowSubmitQuote] = useState(false);
  const [showDeclineQuote, setShowDeclineQuote] = useState(false);
  const [showReviseQuote, setShowReviseQuote] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [quotePrice, setQuotePrice] = useState('');
  const [revisePrice, setRevisePrice] = useState('');
  const [shipmentDetails, setShipmentDetails] = useState({
    pickupTimeStart: '',
    pickupTimeEnd: '',
    deliveryTimeStart: '',
    deliveryTimeEnd: '',
    pickupAddress: '',
    deliveryAddress: '',
    pickupContactName: '',
    pickupContactPhone: '',
    deliveryContactName: '',
    deliveryContactPhone: '',
  });
  const [panelRatingStars, setPanelRatingStars] = useState(0);
  const [panelRatingHover, setPanelRatingHover] = useState(0);
  const [showConfirmDeliveryDialog, setShowConfirmDeliveryDialog] = useState(false);
  const [confirmCloseLoadOpen, setConfirmCloseLoadOpen] = useState(false);

  const closeLoadMutation = useMutation({
    mutationFn: async (loadId: string) => {
      await apiRequest('PATCH', `/api/loads/${loadId}/close`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      toast({ title: 'Load closed', description: 'This load is no longer visible to carriers.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to close load. Please try again.', variant: 'destructive' });
    },
  });

  const confirmDeliveryMutation = useMutation({
    mutationFn: async (loadId: string) => {
      return apiRequest('POST', `/api/loads/${loadId}/confirm-delivery`);
    },
    onSuccess: () => {
      setShowConfirmDeliveryDialog(false);
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
    },
    onError: () => {
      toast({ title: 'Failed to confirm delivery', description: 'Please try again.', variant: 'destructive' });
    },
  });

  const { data: ratingStatus, refetch: refetchRatingStatus } = useQuery<{
    myRating: number | null;
    mySkipped: boolean;
    partnerRating: number | null;
    bothResolved: boolean;
  }>({
    queryKey: ['/api/shipment-ratings/thread', selectedThread?.threadId],
    enabled: (selectedThread?.threadPhase === 'completed' || selectedThread?.threadPhase === 'closed') && !!selectedThread?.threadId,
  });

  const submitPanelRatingMutation = useMutation({
    mutationFn: async ({ stars }: { stars: number }) => {
      if (!selectedThread?.threadId || !selectedThread?.loadId || !selectedThread?.partnerId) throw new Error('Missing thread info');
      return apiRequest('POST', '/api/shipment-ratings', {
        threadId: selectedThread.threadId,
        loadId: selectedThread.loadId,
        toUserId: selectedThread.partnerId,
        rating: stars,
      });
    },
    onSuccess: () => {
      refetchRatingStatus();
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      toast({ title: 'Rating submitted', description: 'Thank you for rating your partner.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to submit rating. Please try again.', variant: 'destructive' });
    },
  });

  const skipPanelRatingMutation = useMutation({
    mutationFn: async () => {
      if (!selectedThread?.threadId || !selectedThread?.loadId || !selectedThread?.partnerId) throw new Error('Missing thread info');
      return apiRequest('POST', '/api/shipment-ratings/skip', {
        threadId: selectedThread.threadId,
        loadId: selectedThread.loadId,
        toUserId: selectedThread.partnerId,
      });
    },
    onSuccess: () => {
      refetchRatingStatus();
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to skip rating.', variant: 'destructive' });
    },
  });

  const saveShipmentDetailsMutation = useMutation({
    mutationFn: ({ loadId, ...details }: { loadId: string } & Record<keyof typeof shipmentDetails, string | null>) =>
      apiRequest('PATCH', `/api/loads/${loadId}`, details),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      toast({ title: 'Shipment details saved', description: 'Pickup and delivery details have been saved.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to save shipment details. Please try again.', variant: 'destructive' });
    },
  });

  useEffect(() => {
    const selectedLoad = selectedThread?.load;
    const toTimeInputValue = (value?: string | null) => value?.slice(0, 5) ?? '';
    setShipmentDetails({
      pickupTimeStart: toTimeInputValue(selectedLoad?.pickupTimeStart),
      pickupTimeEnd: toTimeInputValue(selectedLoad?.pickupTimeEnd),
      deliveryTimeStart: toTimeInputValue(selectedLoad?.deliveryTimeStart),
      deliveryTimeEnd: toTimeInputValue(selectedLoad?.deliveryTimeEnd),
      pickupAddress: selectedLoad?.pickupAddress ?? '',
      deliveryAddress: selectedLoad?.deliveryAddress ?? '',
      pickupContactName: selectedLoad?.pickupContactName ?? '',
      pickupContactPhone: selectedLoad?.pickupContactPhone ?? '',
      deliveryContactName: selectedLoad?.deliveryContactName ?? '',
      deliveryContactPhone: selectedLoad?.deliveryContactPhone ?? '',
    });
  }, [
    selectedThread?.threadId,
    selectedThread?.load?.id,
    selectedThread?.load?.pickupTimeStart,
    selectedThread?.load?.pickupTimeEnd,
    selectedThread?.load?.deliveryTimeStart,
    selectedThread?.load?.deliveryTimeEnd,
    selectedThread?.load?.pickupAddress,
    selectedThread?.load?.deliveryAddress,
    selectedThread?.load?.pickupContactName,
    selectedThread?.load?.pickupContactPhone,
    selectedThread?.load?.deliveryContactName,
    selectedThread?.load?.deliveryContactPhone,
  ]);

  const isShipperRole =
    user?.userType === 'Shipper' ||
    (user?.userType === 'Broker' && selectedThread?.load?.userId === user?.id);
  const isCarrierRole =
    user?.userType === 'Carrier' ||
    (user?.userType === 'Broker' && selectedThread?.load?.userId !== user?.id);

  const activeStep = getActiveStep(selectedThread);

  const latestQuoteMessage = messages
    ?.filter(m => m.messageType === 'quote_submission')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const quoteAmount = latestQuoteMessage?.quotePrice && latestQuoteMessage.quotePrice > 0
    ? (latestQuoteMessage.quotePrice / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : undefined;

  const showQuoteSection =
    !!latestQuoteMessage ||
    selectedThread?.threadPhase === 'pre_quote_question' ||
    selectedThread?.threadPhase === 'quote_submitted' ||
    selectedThread?.threadPhase === 'active_shipment';

  const quoteIsPending = latestQuoteMessage?.claimInvitationStatus === 'pending';
  const quoteIsAccepted = latestQuoteMessage?.claimInvitationStatus === 'accepted';
  const quoteIsDeclined = latestQuoteMessage?.claimInvitationStatus === 'declined';

  const showHotspot = isShipperRole && !((user?.completedShipments ?? 0) >= 3);
  const showShipmentDetailsForm =
    isShipperRole &&
    (selectedThread?.threadPhase === 'active_shipment' || selectedThread?.threadPhase === 'completed');

  const load = selectedThread?.load;

  const showConfirmDelivery = (() => {
    if (!isShipperRole || selectedThread?.threadPhase !== 'active_shipment' || !load?.deliveryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deliveryDay = new Date(load.deliveryDate);
    deliveryDay.setHours(0, 0, 0, 0);
    return deliveryDay <= today;
  })();

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="hidden md:flex flex-col border-r flex-shrink-0 w-[280px] overflow-hidden">
      {/* Sub-section 1: Progress Tracker */}
      <div className="px-4 py-3 border-b flex-shrink-0">
        <p className="text-sm font-semibold text-foreground mb-3">
          Shipment Progress
        </p>
        <div className="space-y-2">
          {SHIPMENT_STEPS.map((step, idx) => {
            const stepNum = idx + 1;
            const isCompleted = stepNum < activeStep;
            const isCurrent = stepNum === activeStep;
            return (
              <div key={step} className="flex items-center gap-2.5">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCompleted
                      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                      : isCurrent
                      ? 'bg-orange-50 text-orange-500 dark:bg-orange-950 dark:text-orange-400'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : isCurrent ? (
                    <ArrowRight className="w-3 h-3" />
                  ) : (
                    <span className="text-[10px] font-semibold">{stepNum}</span>
                  )}
                </div>
                <span
                  className={`text-xs leading-tight ${
                    isCompleted
                      ? 'text-green-700 dark:text-green-300'
                      : isCurrent
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground'
                  }`}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Scrollable lower sections */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Sub-section 2: Current Quote */}
        {showQuoteSection && (
          <div className="px-4 py-3 border-b">
            <p className="text-sm font-semibold text-foreground mb-2">
              Current Quote
            </p>

            {selectedThread?.threadPhase === 'active_shipment' ? (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400">Booking confirmed</p>
                  {quoteAmount && (
                    <p className="text-xs text-muted-foreground">${quoteAmount} accepted</p>
                  )}
                </div>
              </div>
            ) : latestQuoteMessage ? (
              <>
                <p className="text-2xl font-semibold text-orange-500 mb-1">{quoteAmount ? `$${quoteAmount}` : 'Quote submitted'}</p>
                <p className="text-xs text-muted-foreground mb-3">
                  {quoteIsAccepted
                    ? 'Quote accepted'
                    : quoteIsDeclined
                    ? 'Quote declined'
                    : latestQuoteMessage
                    ? `Submitted ${formatTime(latestQuoteMessage.createdAt)}`
                    : 'Awaiting quote'}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mb-3">No quote submitted yet.</p>
            )}

            {/* Shipper: Accept / Decline */}
            {isShipperRole && quoteIsPending && (
              loadAlreadyBooked ? (
                <Badge variant="outline" className="text-muted-foreground text-xs" data-testid="badge-load-already-booked">
                  Load already booked
                </Badge>
              ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {showHotspot && (
                    <div className="relative inline-flex items-center justify-center w-4 h-4 flex-shrink-0">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-orange-500 opacity-75 animate-ping" />
                      <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-orange-500" />
                    </div>
                  )}
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={onCompareBids}
                    data-testid="button-panel-compare-bids"
                  >
                    <LayoutGrid className="w-3.5 h-3.5 mr-1" />
                    Compare &amp; accept bids
                  </Button>
                </div>
              </div>
              )
            )}

            {/* Carrier: Revise Quote */}
            {isCarrierRole && quoteIsPending && !showSubmitQuote && !showDeclineQuote && !showReviseQuote && (
              <Button
                size="sm"
                className="bg-orange-500 text-white"
                onClick={() => {
                  setRevisePrice(quoteAmount || '');
                  setShowReviseQuote(true);
                }}
                data-testid="button-panel-revise-quote"
              >
                <DollarSign className="w-3.5 h-3.5 mr-1" />
                Revise Quote
              </Button>
            )}

            {/* Carrier: Revise Quote inline form */}
            {showReviseQuote && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-muted-foreground">
                  Current: <span className="font-medium text-foreground">${quoteAmount || '—'}</span>
                </p>
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Revised price"
                    value={revisePrice}
                    onChange={e => setRevisePrice(e.target.value)}
                    className="h-8"
                    data-testid="input-panel-revise-price"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      if (!revisePrice || parseFloat(revisePrice) <= 0) return;
                      submitQuoteFromThreadMutation.mutate({
                        price: revisePrice,
                        message: `Revised from $${quoteAmount || '?'} → $${revisePrice}`,
                      });
                      setShowReviseQuote(false);
                      setRevisePrice('');
                    }}
                    disabled={!revisePrice || parseFloat(revisePrice) <= 0 || submitQuoteFromThreadMutation.isPending}
                    data-testid="button-panel-confirm-revise"
                  >
                    {submitQuoteFromThreadMutation.isPending ? 'Submitting...' : `Submit $${revisePrice || '0'}`}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setShowReviseQuote(false); setRevisePrice(''); }}
                    data-testid="button-panel-cancel-revise"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Carrier: Submit Quote / Decline to Quote (pre_quote_question phase) */}
            {isCarrierRole &&
              selectedThread?.threadPhase === 'pre_quote_question' &&
              !showSubmitQuote &&
              !showDeclineQuote && (
                <div className="flex gap-2 mt-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => setShowSubmitQuote(true)}
                    data-testid="button-panel-submit-quote"
                  >
                    <DollarSign className="w-3.5 h-3.5 mr-1" />
                    Submit Quote
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowDeclineQuote(true)}
                    data-testid="button-panel-decline-to-quote"
                    title="Decline to quote"
                  >
                    <ThumbsDown className="w-3.5 h-3.5" />
                  </Button>
                </div>
              )}

            {/* Submit Quote inline form */}
            {showSubmitQuote && (
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Your price"
                    value={quotePrice}
                    onChange={e => setQuotePrice(e.target.value)}
                    className="h-8"
                    data-testid="input-panel-quote-price"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => {
                      submitQuoteFromThreadMutation.mutate({ price: quotePrice });
                      setShowSubmitQuote(false);
                      setQuotePrice('');
                    }}
                    disabled={
                      !quotePrice ||
                      parseFloat(quotePrice) <= 0 ||
                      submitQuoteFromThreadMutation.isPending
                    }
                    data-testid="button-panel-confirm-quote"
                  >
                    {submitQuoteFromThreadMutation.isPending
                      ? 'Submitting...'
                      : `Submit $${quotePrice || '0'}`}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowSubmitQuote(false);
                      setQuotePrice('');
                    }}
                    data-testid="button-panel-cancel-quote"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Decline to Quote inline form */}
            {showDeclineQuote && (
              <div className="mt-2 space-y-2">
                <Select value={declineReason} onValueChange={setDeclineReason}>
                  <SelectTrigger className="h-8 text-xs md:text-xs" data-testid="select-panel-decline-reason">
                    <SelectValue placeholder="Select reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="route_not_suitable">Route not suitable</SelectItem>
                    <SelectItem value="schedule_conflict">Schedule conflict</SelectItem>
                    <SelectItem value="equipment_mismatch">Equipment mismatch</SelectItem>
                    <SelectItem value="capacity_unavailable">Capacity unavailable</SelectItem>
                    <SelectItem value="rate_expectations">Rate too low</SelectItem>
                    <SelectItem value="load_too_small">Load not efficient</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    className="flex-1"
                    onClick={() => {
                      declineToQuoteMutation.mutate({ reason: declineReason });
                      setShowDeclineQuote(false);
                      setDeclineReason('');
                    }}
                    disabled={!declineReason || declineToQuoteMutation.isPending}
                    data-testid="button-panel-confirm-decline"
                  >
                    {declineToQuoteMutation.isPending ? 'Declining...' : 'Confirm'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setShowDeclineQuote(false);
                      setDeclineReason('');
                    }}
                    data-testid="button-panel-cancel-decline"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Shipper guidance: prompt to set shipment details immediately after booking */}
        {isShipperRole &&
          selectedThread?.threadPhase === 'active_shipment' &&
          !shipmentDetails.pickupTimeStart &&
          !shipmentDetails.pickupTimeEnd &&
          !shipmentDetails.deliveryTimeStart &&
          !shipmentDetails.deliveryTimeEnd &&
          !shipmentDetails.pickupAddress &&
          !shipmentDetails.deliveryAddress && (
          <div className="px-4 pt-3">
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3 text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium">Load booked — provide shipment details</p>
              <p className="text-blue-600 dark:text-blue-400 mt-1">
                Add pickup and delivery details so {selectedThread?.partner.firstName ?? 'your carrier'} can prepare.
              </p>
            </div>
          </div>
        )}

        {/* Combined pickup and delivery details form (shipper only) */}
        {showShipmentDetailsForm && (
          <div className="px-4 py-3 border-b">
            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-3">
              Confirm Shipment Details
            </p>
            {(['pickup', 'delivery'] as const).map((location) => {
              const label = location === 'pickup' ? 'Pickup' : 'Delivery';
              const startKey = `${location}TimeStart` as 'pickupTimeStart' | 'deliveryTimeStart';
              const endKey = `${location}TimeEnd` as 'pickupTimeEnd' | 'deliveryTimeEnd';
              const addressKey = `${location}Address` as 'pickupAddress' | 'deliveryAddress';
              const contactNameKey = `${location}ContactName` as 'pickupContactName' | 'deliveryContactName';
              const contactPhoneKey = `${location}ContactPhone` as 'pickupContactPhone' | 'deliveryContactPhone';
              const setField = (field: keyof typeof shipmentDetails, value: string) =>
                setShipmentDetails(current => ({ ...current, [field]: value }));

              return (
                <div key={location} className="mb-4 last:mb-3">
                  <p className="text-sm font-semibold mb-2">{label}</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-muted-foreground block mb-1">Time window</label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1" htmlFor={`${location}-time-start`}>
                            Start
                          </label>
                          <Input
                            id={`${location}-time-start`}
                            type="time"
                            value={shipmentDetails[startKey]}
                            onChange={e => setField(startKey, e.target.value)}
                            className="h-8"
                            data-testid={`input-${location}-time-start`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1" htmlFor={`${location}-time-end`}>
                            End
                          </label>
                          <Input
                            id={`${location}-time-end`}
                            type="time"
                            value={shipmentDetails[endKey]}
                            onChange={e => setField(endKey, e.target.value)}
                            className="h-8"
                            data-testid={`input-${location}-time-end`}
                          />
                        </div>
                      </div>
                    </div>
                    <Input
                      placeholder={`${label} address`}
                      aria-label={`${label} address`}
                      value={shipmentDetails[addressKey]}
                      onChange={e => setField(addressKey, e.target.value)}
                      className="h-8"
                      data-testid={`input-${location}-address`}
                    />
                    <Input
                      placeholder="Contact name"
                      aria-label={`${label} contact name`}
                      value={shipmentDetails[contactNameKey]}
                      onChange={e => setField(contactNameKey, e.target.value)}
                      className="h-8"
                      data-testid={`input-${location}-contact-name`}
                    />
                    <Input
                      type="tel"
                      placeholder="Contact phone"
                      aria-label={`${label} contact phone`}
                      value={shipmentDetails[contactPhoneKey]}
                      onChange={e => setField(contactPhoneKey, e.target.value)}
                      className="h-8"
                      data-testid={`input-${location}-contact-phone`}
                    />
                  </div>
                </div>
              );
            })}
            <Button
              size="sm"
              className="w-full"
              disabled={!selectedThread?.load?.id || saveShipmentDetailsMutation.isPending}
              onClick={() => {
                if (!selectedThread?.load?.id) return;
                saveShipmentDetailsMutation.mutate({
                  loadId: selectedThread.load.id,
                  pickupTimeStart: shipmentDetails.pickupTimeStart || null,
                  pickupTimeEnd: shipmentDetails.pickupTimeEnd || null,
                  deliveryTimeStart: shipmentDetails.deliveryTimeStart || null,
                  deliveryTimeEnd: shipmentDetails.deliveryTimeEnd || null,
                  pickupAddress: shipmentDetails.pickupAddress || null,
                  deliveryAddress: shipmentDetails.deliveryAddress || null,
                  pickupContactName: shipmentDetails.pickupContactName || null,
                  pickupContactPhone: shipmentDetails.pickupContactPhone || null,
                  deliveryContactName: shipmentDetails.deliveryContactName || null,
                  deliveryContactPhone: shipmentDetails.deliveryContactPhone || null,
                });
              }}
              data-testid="button-save-shipment-details"
            >
              {saveShipmentDetailsMutation.isPending ? 'Saving...' : 'Save Details'}
            </Button>
          </div>
        )}

        {/* Confirm Delivery */}
        {showConfirmDelivery && (
          <div className="px-4 py-3 border-b">
            <Button
              size="sm"
              className="w-full bg-green-600 text-white"
              onClick={() => setShowConfirmDeliveryDialog(true)}
              disabled={confirmDeliveryMutation.isPending}
              data-testid="button-confirm-delivery"
            >
              <CheckCircle className="w-3.5 h-3.5 mr-1" />
              {confirmDeliveryMutation.isPending ? 'Confirming…' : 'Confirm Delivery'}
            </Button>
            <p className="text-xs text-muted-foreground mt-2">
              Confirm that this shipment was delivered successfully.
            </p>
          </div>
        )}

        {/* Sub-section 3: Pickup and delivery scheduling details */}
        {load && (
          <div className="px-4 py-3">
            <ShipmentLoadDetails load={load} />
          </div>
        )}

        {/* Sub-section 4: Manage Shipment */}
        {isShipperRole && load && (load.status === 'Available' || load.status === 'Assigned (External)') && (
          <div className="px-4 py-3 border-t">
            <p className="text-sm font-semibold text-foreground mb-3">
              Manage Shipment
            </p>
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              onClick={() => setConfirmCloseLoadOpen(true)}
              disabled={closeLoadMutation.isPending}
              data-testid="button-close-load-workspace"
            >
              <XCircle className="w-3.5 h-3.5 mr-1.5" />
              {closeLoadMutation.isPending ? 'Closing...' : 'Close Load'}
            </Button>
            <AlertDialog open={confirmCloseLoadOpen} onOpenChange={setConfirmCloseLoadOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Close this load?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action closes all currently active negotiations and removes the load from the marketplace — carriers will no longer be able to find or quote on it.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="button-cancel-close-load-workspace">Keep Load</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => { setConfirmCloseLoadOpen(false); load.id && closeLoadMutation.mutate(load.id); }}
                    data-testid="button-confirm-close-load-workspace"
                  >
                    Close Load
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {/* Sub-section 5: Rating States */}
        {(selectedThread?.threadPhase === 'completed' || selectedThread?.threadPhase === 'closed') && selectedThread?.partnerId && (
          <div className="px-4 py-3 border-t">
            <p className="text-sm font-semibold text-foreground mb-2">
              {selectedThread.threadPhase === 'closed' ? 'Shipment Closed' : 'Rate Your Experience'}
            </p>
            {selectedThread.threadPhase === 'closed' ? (
              /* State 3: Closed thread */
              <div className="space-y-2">
                {ratingStatus?.myRating !== null && ratingStatus?.myRating !== undefined ? (
                  <div className="flex items-center gap-1.5">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${n <= (ratingStatus.myRating ?? 0) ? 'text-orange-400 fill-orange-400' : 'text-muted-foreground'}`}
                      />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">Your rating</span>
                  </div>
                ) : ratingStatus?.mySkipped ? (
                  <p className="text-xs text-muted-foreground">Rating skipped.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">This shipment has been closed.</p>
                )}
                {ratingStatus?.partnerRating !== null && ratingStatus?.partnerRating !== undefined && (
                  <div className="flex items-center gap-1.5 mt-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${n <= (ratingStatus.partnerRating ?? 0) ? 'text-orange-400 fill-orange-400' : 'text-muted-foreground'}`}
                      />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1">Their rating</span>
                  </div>
                )}
              </div>
            ) : ratingStatus?.myRating !== null && ratingStatus?.myRating !== undefined ? (
              /* State 2: Already rated */
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 4, 5].map(n => (
                    <Star
                      key={n}
                      className={`w-4 h-4 ${n <= (ratingStatus.myRating ?? 0) ? 'text-orange-400 fill-orange-400' : 'text-muted-foreground'}`}
                    />
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">You rated {selectedThread.partner.firstName} {ratingStatus.myRating}/5 stars.</p>
                {!ratingStatus.partnerRating && (
                  <p className="text-xs text-muted-foreground italic">Waiting for their rating…</p>
                )}
              </div>
            ) : ratingStatus?.mySkipped ? (
              /* State 2b: Rating skipped */
              <p className="text-xs text-muted-foreground">You skipped rating for this shipment.</p>
            ) : (
              /* State 1: Pending rating */
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">How did {selectedThread.partner.firstName} do?</p>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setPanelRatingHover(n)}
                      onMouseLeave={() => setPanelRatingHover(0)}
                      onClick={() => setPanelRatingStars(n)}
                      className="p-0.5 focus:outline-none"
                      data-testid={`button-panel-star-${n}`}
                    >
                      <Star
                        className={`w-5 h-5 transition-colors ${
                          n <= (panelRatingHover || panelRatingStars)
                            ? 'text-orange-400 fill-orange-400'
                            : 'text-muted-foreground'
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    disabled={!panelRatingStars || submitPanelRatingMutation.isPending}
                    onClick={() => panelRatingStars && submitPanelRatingMutation.mutate({ stars: panelRatingStars })}
                    data-testid="button-panel-submit-rating"
                  >
                    {submitPanelRatingMutation.isPending ? 'Saving…' : 'Submit'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skipPanelRatingMutation.isPending}
                    onClick={() => skipPanelRatingMutation.mutate()}
                    data-testid="button-panel-skip-rating"
                  >
                    Skip
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm Delivery Dialog */}
      <Dialog open={showConfirmDeliveryDialog} onOpenChange={setShowConfirmDeliveryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delivery</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to confirm delivery for this shipment? This will mark the shipment as complete.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDeliveryDialog(false)}
              disabled={confirmDeliveryMutation.isPending}
              data-testid="button-confirm-delivery-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-green-600 text-white"
              onClick={() => {
                if (load?.id) confirmDeliveryMutation.mutate(load.id);
              }}
              disabled={confirmDeliveryMutation.isPending}
              data-testid="button-confirm-delivery-submit"
            >
              {confirmDeliveryMutation.isPending ? 'Confirming…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Messages() {
  const { toast } = useToast();
  const { user, isLoading: authLoading } = useAuth();
  const companyId = user?.accountOwnerId ?? user?.id;
  const { socket, isConnected, sendMessage: socketSendMessage, sendTypingStatus, markAsRead, setViewingConversation, typingUsers } = useSocket();
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [location, setLocation] = useLocation();
  const [newMessage, setNewMessage] = useState("");
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [selectedThreadLoadId, setSelectedThreadLoadId] = useState<string | null>(null); // Load ID for current thread
  const [showClaimInvitation, setShowClaimInvitation] = useState(false);
  const [claimLoadId, setClaimLoadId] = useState<string>(""); // Load ID for claim invitation modal
  const [isSending, setIsSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<"All" | "Active" | "Quotes" | "Closed">("All");
  const [pendingAttachment, setPendingAttachment] = useState<{
    url: string;
    name: string;
    type: string;
    size: number;
  } | null>(null);
  const [loadModalOpen, setLoadModalOpen] = useState(false);
  const [loadModalData, setLoadModalData] = useState<any>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingModalAllowOverride, setRatingModalAllowOverride] = useState(false);
  const [showDisputeBanner, setShowDisputeBanner] = useState(false);
  const shownRatingPromptRef = useRef<Set<string>>(new Set());
  const pendingDeepLinkRef = useRef<{ loadId: string; threadId?: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { uploadFile, isUploading } = useUpload({
    onSuccess: (response) => {
      setPendingAttachment({
        url: response.objectPath,
        name: response.metadata.name,
        type: response.metadata.contentType,
        size: response.metadata.size,
      });
    },
    onError: (error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Listen for URL changes (including query params) and parse them
  useEffect(() => {
    const handleUrlChange = () => {
      // Only process if we're on the messages page
      if (!window.location.pathname.includes('/messages') && !window.location.pathname.includes('/shipments')) return;
      
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('user');
      const loadId = params.get('load');
      
      if (userId) {
        setSelectedPartnerId(userId);
        setSelectedThreadLoadId(loadId || null);
      }
      // Note: We don't clear selection when params are removed - users may want to stay in current thread
    };
    
    // Parse on initial mount
    handleUrlChange();
    
    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleUrlChange);
    
    // Patch pushState/replaceState to emit custom events for programmatic navigation
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);
    
    history.pushState = (...args) => {
      originalPushState(...args);
      handleUrlChange();
    };
    
    history.replaceState = (...args) => {
      originalReplaceState(...args);
      handleUrlChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  // Handle deep link params: ?load=[loadId] and ?load=[loadId]&thread=[threadId]
  // Read once on mount, clear from URL, resolve when threads load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const loadId = params.get('load');
    const threadId = params.get('thread');
    const userId = params.get('user');
    // Only handle new-style deep links (no ?user= param — that's handled by the existing listener)
    if (loadId && !userId) {
      pendingDeepLinkRef.current = { loadId, threadId: threadId || undefined };
      params.delete('load');
      params.delete('thread');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Handle special URL params: dispute, rate, delivered, already, expired
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dispute') === 'true') {
      setShowDisputeBanner(true);
      params.delete('dispute');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    if (params.get('already') === 'confirmed') {
      toast({ title: 'Shipment already completed', description: 'This shipment was already marked as completed.' });
      params.delete('already');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    if (params.get('expired') === 'true') {
      toast({ title: 'Delivery window expired', description: 'The delivery window has passed. The shipment has been auto-completed.' });
      params.delete('expired');
      const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Fetch load-based threads (grouped by partner + load)
  const { data: threads, isLoading: threadsLoading } = useQuery<LoadThread[]>({
    queryKey: ['/api/messages/threads'],
    enabled: !!user,
  });

  // Resolve pending deep link once threads are loaded
  useEffect(() => {
    if (!threads || !pendingDeepLinkRef.current) return;
    const { loadId, threadId } = pendingDeepLinkRef.current;
    pendingDeepLinkRef.current = null; // consume so it only runs once
    if (threadId) {
      const thread = threads.find(t => t.threadId === threadId);
      if (thread) {
        setSelectedPartnerId(thread.partnerId);
        setSelectedThreadLoadId(thread.loadId);
      }
    } else {
      const loadThreads = threads.filter(t => t.loadId === loadId);
      if (loadThreads.length === 1) {
        setSelectedPartnerId(loadThreads[0].partnerId);
        setSelectedThreadLoadId(loadThreads[0].loadId);
      } else if (loadThreads.length > 1) {
        // Multiple threads for this load — prefer the active_shipment one, otherwise the first
        const preferred = loadThreads.find(t => t.threadPhase === 'active_shipment') || loadThreads[0];
        setSelectedPartnerId(preferred.partnerId);
        setSelectedThreadLoadId(preferred.loadId);
      }
    }
  }, [threads]);

  // Legacy conversations query (for backward compatibility)
  const { data: conversations, isLoading: conversationsLoading } = useQuery<ConversationPartner[]>({
    queryKey: ['/api/messages/conversations'],
    enabled: !!user,
  });

  // Fetch messages for selected thread (uses loadId when available)
  const { data: messages, isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'],
    enabled: !!user && !!selectedPartnerId,
  });

  const latestQuoteMessageForBubble = messages
    ?.filter(m => m.messageType === 'quote_submission')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

  const quoteAmountForBubble = latestQuoteMessageForBubble?.quotePrice && latestQuoteMessageForBubble.quotePrice > 0
    ? (latestQuoteMessageForBubble.quotePrice / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : undefined;

  const { data: myLoads } = useQuery<Load[]>({
    queryKey: ['/api/loads/user', user?.id],
    enabled: !!user && (user.userType === "Shipper" || user.userType === "Broker"),
  });

  const availableLoads = myLoads?.filter(load => !load.claimedByUserId && load.status === "Posted") || [];

  const filteredThreads = useMemo(() => {
    if (!threads) return [];
    const inactivePhases = ['closed', 'auto_closed', 'quote_declined'];
    const base = !searchQuery.trim() ? threads : threads.filter(t =>
      `${t.partner.firstName} ${t.partner.lastName}`.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
      t.partner.firstName?.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
      t.partner.lastName?.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
      t.partner.company?.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
      t.load?.loadIdentifier?.toLowerCase().includes(searchQuery.toLowerCase().trim())
    );
    return [...base].sort((a, b) => {
      const aInactive = inactivePhases.includes(a.threadPhase ?? '');
      const bInactive = inactivePhases.includes(b.threadPhase ?? '');
      if (aInactive !== bInactive) return aInactive ? 1 : -1;
      return new Date(b.latestMessage?.createdAt ?? 0).getTime() - new Date(a.latestMessage?.createdAt ?? 0).getTime();
    });
  }, [threads, searchQuery]);

  const isDeepLinkedNewConversation = selectedPartnerId && !conversations?.find(c => c.partnerId === selectedPartnerId);
  
  const { data: deepLinkedPartner } = useQuery<{
    id: string;
    firstName: string;
    lastName: string;
    company: string | null;
    companyLogo: string | null;
    userType: string;
  }>({
    queryKey: ['/api/users', selectedPartnerId, 'public'],
    enabled: !!isDeepLinkedNewConversation,
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages) {
      scrollToBottom();
      // Refresh threads to update unread counts after messages are marked as read
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
    }
  }, [messages]);

  // Mark messages as read for the current thread (partner + load combination)
  useEffect(() => {
    if (selectedPartnerId && isConnected) {
      // Use thread-specific mark-as-read via HTTP (loadId-aware)
      const loadIdParam = selectedThreadLoadId || 'general';
      apiRequest("POST", `/api/messages/thread/mark-read/${selectedPartnerId}/${loadIdParam}`, {})
        .catch(console.error);
      
      // Still notify socket about viewing for typing indicators (partner-level is fine for this)
      setViewingConversation(selectedPartnerId);
    }
    
    return () => {
      if (isConnected) {
        setViewingConversation(null);
      }
    };
  }, [selectedPartnerId, selectedThreadLoadId, isConnected, setViewingConversation]);

  useEffect(() => {
    setPendingAttachment(null);
  }, [selectedPartnerId, selectedThreadLoadId]);

  const handleTyping = useCallback(() => {
    if (!selectedPartnerId || !isConnected) return;
    
    sendTypingStatus(selectedPartnerId, true);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus(selectedPartnerId, false);
    }, 2000);
  }, [selectedPartnerId, isConnected, sendTypingStatus]);

  const isPartnerTyping = selectedPartnerId ? typingUsers.get(selectedPartnerId) : false;

  const sendMutation = useMutation({
    mutationFn: async (messageText: string) => {
      return apiRequest("POST", "/api/messages", {
        toUserId: selectedPartnerId,
        message: messageText,
        loadId: selectedThreadLoadId || undefined,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      setNewMessage("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send message",
        variant: "destructive",
      });
    },
  });

  const sendClaimInvitationMutation = useMutation({
    mutationFn: async ({ loadId, message }: { loadId: string; message: string }) => {
      return apiRequest("POST", "/api/messages/claim-invitation", {
        toUserId: selectedPartnerId,
        loadId,
        message,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads/user', user?.id] });
      setNewMessage("");
      setClaimLoadId("");
      setShowClaimInvitation(false);
      toast({
        title: "Claim invitation sent",
        description: "The carrier can now accept and claim your load",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send claim invitation",
        variant: "destructive",
      });
    },
  });

  // Submit Quote mutation (carrier submitting a quote from the messages page)
  const submitQuoteFromThreadMutation = useMutation({
    mutationFn: async ({ price, message }: { price: string; message?: string }) => {
      if (!selectedThreadLoadId || !selectedPartnerId) {
        throw new Error("No load thread selected");
      }
      return apiRequest("POST", "/api/messages/quote-submission", {
        toUserId: selectedPartnerId,
        loadId: selectedThreadLoadId,
        price,
        message,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      toast({
        title: "Quote submitted",
        description: "The shipper has been notified of your quote.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to submit quote",
        variant: "destructive",
      });
    },
  });

  // Decline to Quote mutation (carrier declining to provide a quote) - transitions thread to quote_declined phase
  const declineToQuoteMutation = useMutation({
    mutationFn: async ({ reason }: { reason: string }) => {
      if (!selectedThreadLoadId || !selectedPartnerId) {
        throw new Error("No load thread selected");
      }
      return apiRequest("POST", "/api/messages/decline-to-quote", {
        loadId: selectedThreadLoadId,
        shipperId: selectedPartnerId,
        reason,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      toast({
        title: "Declined to quote",
        description: "The shipper has been notified. This thread is now closed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to decline",
        variant: "destructive",
      });
    },
  });

  const acceptClaimMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return apiRequest("POST", `/api/messages/${messageId}/accept-claim`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      queryClient.invalidateQueries({ queryKey: ['/api/loads/claimed/me'] });
      toast({
        title: "Load claimed successfully",
        description: "You have accepted the load. Check your Dashboard for details.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to accept claim",
        variant: "destructive",
      });
    },
  });

  const acceptQuoteMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return apiRequest("POST", `/api/messages/${messageId}/accept-quote`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      toast({
        title: "Quote accepted",
        description: "Bid accepted — the load is now booked.",
      });
    },
    onError: (error: Error) => {
      const isAlreadyBooked = error.message.startsWith('409');
      toast({
        title: isAlreadyBooked ? "Load already booked" : "Unable to accept quote",
        description: isAlreadyBooked
          ? "This load has already been booked with another carrier. You cannot accept another quote for this load."
          : error.message.startsWith("403: Your subscription") || error.message === "This listing is no longer available."
            ? error.message
            : "Something went wrong. Please try again.",
        variant: "destructive",
      });
    },
  });

  const declineQuoteMutation = useMutation({
    mutationFn: async (messageId: string) => {
      return apiRequest("POST", `/api/messages/${messageId}/decline-quote`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/threads'] });
      toast({
        title: "Quote declined",
        description: "The carrier has been notified.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to decline quote",
        variant: "destructive",
      });
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !pendingAttachment) || !selectedPartnerId) return;

    if (!user?.emailVerified) {
      setShowEmailVerificationModal(true);
      return;
    }
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      sendTypingStatus(selectedPartnerId, false);
    }
    
    if (isConnected) {
      setIsSending(true);
      try {
        const result = await socketSendMessage(
          selectedPartnerId,
          newMessage.trim() || (pendingAttachment?.name ? `Sent ${pendingAttachment.name}` : 'Sent an attachment'),
          pendingAttachment ? 'attachment' : 'text',
          pendingAttachment || undefined,
          selectedThreadLoadId || undefined
        );
        if (result) {
          setNewMessage("");
          setPendingAttachment(null);
        } else {
          sendMutation.mutate(newMessage);
        }
      } catch (error) {
        toast({
          title: "Unable to send message",
          description: error instanceof Error ? error.message : "Failed to send message",
          variant: "destructive",
        });
      } finally {
        setIsSending(false);
      }
    } else {
      sendMutation.mutate(newMessage);
    }
  };

  const handleSendClaimInvitation = () => {
    if (claimLoadId && selectedPartnerId) {
      sendClaimInvitationMutation.mutate({
        loadId: claimLoadId,
        message: newMessage.trim() || "I'd like to offer you this load. Click below to claim it.",
      });
    }
  };

  const handleAcceptClaim = (messageId: string) => {
    acceptClaimMutation.mutate(messageId);
  };

  const handleAcceptQuote = (messageId: string) => {
    acceptQuoteMutation.mutate(messageId);
  };

  const handleDeclineQuote = (messageId: string) => {
    declineQuoteMutation.mutate(messageId);
  };

  const handleLoadBadgeClick = async (loadId: string) => {
    try {
      const response = await fetch(`/api/loads/${loadId}`);
      if (response.ok) {
        const loadWithItems = await response.json();
        setLoadModalData(loadWithItems);
        setLoadModalOpen(true);
      } else {
        const error = await response.json().catch(() => ({}));
        toast({
          title: "Could not load details",
          description: error.listingUnavailable ? error.message : "Unable to fetch load information.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Could not load details",
        description: "Unable to fetch load information.",
        variant: "destructive",
      });
    }
  };

  const canSendClaimInvitation = user?.userType === "Shipper" || user?.userType === "Broker";

  const existingConversation = conversations?.find(c => c.partnerId === selectedPartnerId);
  const selectedPartnerInfo = existingConversation?.partner || deepLinkedPartner;
  
  // Cache the selected thread info to avoid repeated lookups
  const selectedThread = useMemo(() => {
    if (!selectedThreadLoadId || !threads) return null;
    return threads.find(t => t.loadId === selectedThreadLoadId) || null;
  }, [selectedThreadLoadId, threads]);

  const isCarrierRole =
    user?.userType === 'Carrier' ||
    (user?.userType === 'Broker' && selectedThread?.load?.userId !== user?.id);

  const isShipperRole =
    user?.userType === 'Shipper' ||
    (user?.userType === 'Broker' && selectedThread?.load?.userId === user?.id);

  // Thread selection trigger: open rating modal when selecting a completed thread (if not yet rated)
  useEffect(() => {
    const threadId = selectedThread?.threadId;
    if (!threadId || selectedThread?.threadPhase !== 'completed' || shownRatingPromptRef.current.has(threadId)) return;
    const pendingKey = `pendingCelebration:${selectedThread?.loadId}`;
    const hasPending = localStorage.getItem(pendingKey);
    const checkAndOpen = async () => {
      try {
        const res = await fetch(`/api/shipment-ratings/thread/${threadId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.myRating === null && !data.mySkipped) {
          shownRatingPromptRef.current.add(threadId);
          if (hasPending) localStorage.removeItem(pendingKey);
          setShowRatingModal(true);
        }
      } catch {
        // ignore
      }
    };
    if (hasPending) {
      localStorage.removeItem(pendingKey);
      shownRatingPromptRef.current.add(threadId);
      setShowRatingModal(true);
    } else {
      checkAndOpen();
    }
  }, [selectedThread?.threadId, selectedThread?.threadPhase, selectedThread?.loadId]);

  // Socket trigger: when shipment_completed fires, open modal if thread selected, else store pending
  useEffect(() => {
    if (!socket) return;
    const handleCompleted = ({ loadId }: { loadId: string; loadIdentifier?: string }) => {
      const matchThread = threads?.find(t => t.loadId === loadId);
      if (!matchThread) {
        localStorage.setItem(`pendingCelebration:${loadId}`, '1');
        return;
      }
      const isSelected = matchThread.loadId === selectedThreadLoadId;
      if (isSelected && matchThread.threadPhase === 'completed') {
        const threadId = matchThread.threadId;
        if (threadId && !shownRatingPromptRef.current.has(threadId)) {
          shownRatingPromptRef.current.add(threadId);
          setShowRatingModal(true);
        }
      } else {
        localStorage.setItem(`pendingCelebration:${loadId}`, '1');
      }
    };
    socket.on('shipment_completed', handleCompleted);
    socket.on('shipment_auto_completed', handleCompleted);
    return () => {
      socket.off('shipment_completed', handleCompleted);
      socket.off('shipment_auto_completed', handleCompleted);
    };
  }, [socket, threads, selectedThreadLoadId]);

  // Handle ?delivered=confirmed URL param after threads load
  useEffect(() => {
    if (!threads) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('delivered') !== 'confirmed') return;
    params.delete('delivered');
    const newUrl = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
    window.history.replaceState({}, '', newUrl);
    const completedThread = [...threads]
      .filter(t => t.threadPhase === 'completed')
      .sort((a, b) => new Date(b.latestMessage?.createdAt ?? 0).getTime() - new Date(a.latestMessage?.createdAt ?? 0).getTime())[0];
    if (!completedThread) return;
    const threadId = completedThread.threadId;
    if (!threadId || shownRatingPromptRef.current.has(threadId)) return;
    fetch(`/api/shipment-ratings/thread/${threadId}`)
      .then(r => r.json())
      .then(data => {
        if (data.myRating === null && !data.mySkipped) {
          shownRatingPromptRef.current.add(threadId);
          setSelectedPartnerId(completedThread.partnerId);
          if (completedThread.loadId) setSelectedThreadLoadId(completedThread.loadId);
          setShowRatingModal(true);
        }
      })
      .catch(() => {});
  }, [threads]);

  if (authLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Shipment Workspace</h1>
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">Loading...</p>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold mb-6">Shipment Workspace</h1>
        <Card className="p-6 text-center">
          <p className="text-muted-foreground">Please log in to view messages.</p>
        </Card>
      </div>
    );
  }

  const hasThreads = threads && threads.length > 0;
  const hasConversations = conversations && conversations.length > 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={() => setShowEmailVerificationModal(false)}
      />
      
      {showDisputeBanner && (
        <div className="flex-shrink-0 mx-4 mt-4 flex items-center gap-3 px-4 py-3 rounded-md bg-amber-50 border border-amber-300 text-amber-800 dark:bg-amber-950 dark:border-amber-700 dark:text-amber-200" data-testid="banner-dispute">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
          <span className="text-sm flex-1">A dispute has been opened on one of your shipments. Review the details in your thread and contact support if needed.</span>
          <button onClick={() => setShowDisputeBanner(false)} className="text-amber-600 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 text-lg leading-none">&times;</button>
        </div>
      )}

      <h1 className="text-2xl font-bold mb-3 flex-shrink-0 px-4 pt-4">Shipment Workspace</h1>
      
      <Card className="overflow-hidden flex-1 min-h-0 mx-4 mb-4">
        <div className="flex h-full">
          <div className={`border-r flex flex-col overflow-hidden ${selectedPartnerId ? 'hidden md:flex' : ''} w-full md:w-[300px] flex-shrink-0`}>
            <div className="px-5 py-4 border-b space-y-2 flex-shrink-0">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold">Shipments</h2>
                <span className="text-xs text-muted-foreground">{threads?.length || 0} threads</span>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search shipments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-8"
                  data-testid="input-search-messages"
                />
                {searchQuery && (
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => setSearchQuery("")}
                    data-testid="button-clear-search"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="flex gap-1 mt-3">
                {(["All", "Active", "Quotes", "Closed"] as const).map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActiveFilter(filter)}
                    className={`flex-1 text-xs py-0.5 rounded-full border transition-colors ${
                      activeFilter === filter
                        ? "bg-orange-500 text-white border-orange-500"
                        : "border-border text-foreground"
                    }`}
                    data-testid={`button-filter-${filter.toLowerCase()}`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              {threadsLoading ? (
                <div className="p-4 text-center text-muted-foreground">
                  Loading conversations...
                </div>
              ) : (() => {
                const visibleThreads = filteredThreads.filter(thread => {
                  if (activeFilter === "All") return true;
                  const phase = thread.threadPhase;
                  const loadStatus = thread.load?.status;
                  if (activeFilter === "Active") {
                    return phase === null || phase === 'in_progress' || phase === 'confirmed';
                  }
                  if (activeFilter === "Quotes") {
                    return phase === 'pre_quote_question' || phase === 'quote_submitted';
                  }
                  if (activeFilter === "Closed") {
                    return phase === 'quote_declined' || phase === 'auto_closed' || phase === 'completed' || phase === 'closed' || loadStatus === 'Closed';
                  }
                  return true;
                });

                if (visibleThreads.length > 0) {
                  return (
                    <div className="px-4 py-3 space-y-1">
                      {visibleThreads.map(thread => {
                        const isSelected = selectedPartnerId === thread.partnerId && selectedThreadLoadId === thread.loadId;
                        return (
                          <div
                            key={`${thread.partnerId}-${thread.loadId || 'general'}`}
                            className={selectedPartnerId && !isSelected ? 'opacity-50 transition-opacity duration-200' : 'transition-opacity duration-200'}
                          >
                            <ThreadItem
                              thread={thread}
                              isSelected={isSelected}
                              onClick={() => {
                                setSelectedPartnerId(thread.partnerId);
                                setSelectedThreadLoadId(thread.loadId);
                              }}
                              onLoadClick={handleLoadBadgeClick}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                if (searchQuery.trim()) {
                  return (
                    <div className="p-4 text-center">
                      <p className="text-sm text-muted-foreground">No conversations match your search.</p>
                    </div>
                  );
                }

                return (
                  <div className="p-4 text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">
                      No conversations yet. Contact carriers or shippers from the marketplace to start a conversation.
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Workspace panels: ShipmentDetailsPanel + Chat — soft orange border when a thread is selected */}
          <div className={`flex flex-1 min-w-0 overflow-hidden ${selectedPartnerId ? 'border-t border-r border-b border-orange-500' : ''}`}>
          {selectedPartnerId && (
            <ShipmentDetailsPanel
              selectedThread={selectedThread}
              messages={messages}
              user={user}
              handleAcceptQuote={handleAcceptQuote}
              handleDeclineQuote={handleDeclineQuote}
              acceptQuoteMutation={acceptQuoteMutation}
              declineQuoteMutation={declineQuoteMutation}
              onCompareBids={() => selectedThread?.loadId && setLocation(`/loads/${selectedThread.loadId}/bids`)}
              submitQuoteFromThreadMutation={submitQuoteFromThreadMutation}
              declineToQuoteMutation={declineToQuoteMutation}
              toast={toast}
              loadAlreadyBooked={!!threads?.some(t =>
                t.loadId === selectedThread?.loadId &&
                t.threadId !== selectedThread?.threadId &&
                t.threadPhase === 'active_shipment'
              )}
            />
          )}

          <div className={`flex-1 flex flex-col overflow-hidden ${!selectedPartnerId ? 'hidden md:flex' : ''}`}>
            {selectedPartnerId ? (
              <>
                <div className="px-6 py-4 border-b flex items-center gap-3 bg-card flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden"
                    onClick={() => {
                      setSelectedPartnerId(null);
                      setSelectedThreadLoadId(null);
                    }}
                    data-testid="button-back"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  {selectedPartnerInfo && (
                    <>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <MemberLink 
                            userId={selectedPartnerId!} 
                            name={`${selectedPartnerInfo.firstName} ${selectedPartnerInfo.lastName}`}
                            companyLogo={selectedPartnerInfo.companyLogo}
                            showAvatar
                            avatarSize="md"
                            className="font-medium"
                            data-testid="link-partner-profile"
                          />
                          {selectedThreadLoadId ? (
                            <Badge 
                              variant="outline" 
                              className="font-mono text-xs bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 cursor-pointer" 
                              data-testid="badge-thread-load"
                              onClick={() => {
                                const loadId = selectedThread?.load?.id || selectedThreadLoadId;
                                if (loadId) handleLoadBadgeClick(loadId);
                              }}
                            >
                              {selectedThread?.load?.loadIdentifier || selectedThreadLoadId}
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs" data-testid="badge-thread-general">
                              General
                            </Badge>
                          )}
                          {isShipperRole && selectedThread?.loadId && (
                            <button
                              onClick={() => setLocation(`/loads/${selectedThread.loadId}/bids`)}
                              className="text-xs font-medium text-primary underline hover:no-underline ml-2"
                              data-testid="link-view-bids-workspace"
                            >
                              View bids
                            </button>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground ml-10 truncate">
                          {selectedPartnerInfo.company || selectedPartnerInfo.userType}
                          {selectedThread?.load && (
                            <span className="ml-2">
                              • {selectedThread.load.origin} → {selectedThread.load.destination}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/api/messages/export/${selectedPartnerId}?format=json`, '_blank')}
                          title="Export conversation (JSON)"
                          data-testid="button-export-json"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Export
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                <ScrollArea className="flex-1 min-h-0 px-6 py-4">
                  {messagesLoading ? (
                    <div className="text-center text-muted-foreground">Loading messages...</div>
                  ) : messages && messages.length > 0 ? (
                    <>
                      {messages.map((message, idx) => {
                        const msgDate = new Date(message.createdAt);
                        const prevMsg = idx > 0 ? messages[idx - 1] : null;
                        const prevDate = prevMsg ? new Date(prevMsg.createdAt) : null;
                        const showDateSep = !prevDate || 
                          msgDate.toDateString() !== prevDate.toDateString();

                        // A quote_submission is a revision if an earlier quote_submission
                        // from the same carrier already exists in this thread.
                        const isRevision = message.messageType === 'quote_submission'
                          ? messages.slice(0, idx).some(
                              m => m.messageType === 'quote_submission' && m.fromUserId === message.fromUserId
                            )
                          : false;
                        
                        const formatDateLabel = (d: Date) => {
                          const now = new Date();
                          const isToday = d.toDateString() === now.toDateString();
                          const yesterday = new Date(now);
                          yesterday.setDate(now.getDate() - 1);
                          const isYesterday = d.toDateString() === yesterday.toDateString();
                          if (isToday) return `Today, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                          if (isYesterday) return `Yesterday, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                          return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        };

                        return (
                          <div key={message.id}>
                            {showDateSep && (
                              <div className="flex items-center gap-3 my-6" data-testid={`date-separator-${idx}`}>
                                <div className="flex-1 h-px bg-border" />
                                <span className="text-xs font-medium text-muted-foreground px-3 py-1 bg-muted/50 rounded-full">
                                  {formatDateLabel(msgDate)}
                                </span>
                                <div className="flex-1 h-px bg-border" />
                              </div>
                            )}
                            {message.messageType === 'load_share' ? (
                              <LoadShareCard
                                message={message}
                                isOwn={message.fromUserId === companyId}
                                currentUserId={user.id}
                                onConversationUpdate={() => {
                                  queryClient.invalidateQueries({ queryKey: ["/api/messages/thread", selectedPartnerId, selectedThread?.loadId || 'general'] });
                                }}
                              />
                            ) : (
                              <MessageBubble
                                message={message}
                                isOwn={message.fromUserId === companyId}
                                onAcceptClaim={handleAcceptClaim}
                                isAcceptingClaim={acceptClaimMutation.isPending}
                                onAcceptQuote={handleAcceptQuote}
                                onDeclineQuote={handleDeclineQuote}
                                isAcceptingQuote={acceptQuoteMutation.isPending}
                                isDecliningQuote={declineQuoteMutation.isPending}
                                shipperCompany={selectedThread?.partner?.company}
                                quoteAmount={quoteAmountForBubble}
                                pickupTimeWindow={selectedThread?.load?.pickupTimeWindow}
                                isRevision={isRevision}
                              />
                            )}
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  ) : (
                    <div className="text-center text-muted-foreground">
                      No messages yet. Send a message to start the conversation.
                    </div>
                  )}
                </ScrollArea>

                {isCarrierRole &&
                  selectedThread?.threadPhase === 'active_shipment' &&
                  !selectedThread?.load?.bolUploaded &&
                  selectedThread?.load?.id && (() => {
                    const bolMsg = messages?.find(m => m.messageType === 'bol_upload');
                    return (
                      <BolUploadCard
                        key={selectedThread!.load!.id}
                        loadId={selectedThread!.load!.id}
                        onUploaded={() => {
                          queryClient.invalidateQueries({ queryKey: ['/api/messages/thread', selectedPartnerId, selectedThreadLoadId || 'general'] });
                        }}
                        initialBolUploaded={selectedThread?.load?.bolUploaded ?? false}
                        initialBolFileName={bolMsg?.attachmentName ?? undefined}
                        initialBolFileUrl={bolMsg?.attachmentUrl ?? undefined}
                      />
                    );
                  })()}

                <div className="px-6 py-1.5 border-t bg-muted/30 flex-shrink-0">
                  <p className="text-xs text-muted-foreground text-center" data-testid="text-compliance-notice">
                    Messages can be exported from the Export link in the upper right corner. Keep communications professional.
                  </p>
                </div>

                {showClaimInvitation ? (
                  <div className="p-4 border-t space-y-3 flex-shrink-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium flex items-center gap-2">
                        <Gift className="w-4 h-4 text-primary" />
                        Send Claim Invitation
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowClaimInvitation(false);
                          setClaimLoadId("");
                        }}
                        data-testid="button-cancel-invitation"
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    <Select value={claimLoadId} onValueChange={setClaimLoadId}>
                      <SelectTrigger data-testid="select-load">
                        <SelectValue placeholder="Select a load to offer" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableLoads.map(load => (
                          <SelectItem key={load.id} value={load.id}>
                            {load.loadIdentifier ? `[${load.loadIdentifier}] ` : ''}{load.origin} → {load.destination}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input 
                      placeholder="Add a message (optional)..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      data-testid="input-invitation-message"
                    />
                    <Button 
                      className="w-full"
                      onClick={handleSendClaimInvitation}
                      disabled={!claimLoadId || sendClaimInvitationMutation.isPending}
                      data-testid="button-send-invitation"
                    >
                      <Gift className="w-4 h-4 mr-2" />
                      {sendClaimInvitationMutation.isPending ? "Sending..." : "Send Claim Invitation"}
                    </Button>
                  </div>
                ) : (
                  <div className="px-6 py-4 border-t bg-card flex-shrink-0">
                    <>
                        {/* Declined/Closed Thread Notice */}
                        {(selectedThread?.threadPhase === 'quote_declined' || selectedThread?.threadPhase === 'auto_closed') && (
                          <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg" data-testid="thread-closed-notice">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-500" />
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {selectedThread?.threadPhase === 'quote_declined' 
                                  ? 'This thread was closed after declining to quote.' 
                                  : 'This thread was automatically closed due to inactivity.'}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Rating Prompt — thread completed, awaiting rating */}
                        {selectedThread?.threadPhase === 'completed' && selectedThread?.threadId && selectedThread?.loadId && (
                          <div className="mb-3 p-3 bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-lg" data-testid="rating-prompt-banner">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="flex items-center gap-2">
                                <Star className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                <span className="text-sm text-orange-800 dark:text-orange-200">
                                  Shipment delivered — rate your experience with{' '}
                                  <span className="font-medium">{selectedThread.partner.firstName} {selectedThread.partner.lastName}</span>
                                </span>
                              </div>
                              <Button
                                size="sm"
                                onClick={() => {
                                  setRatingModalAllowOverride(true);
                                  setShowRatingModal(true);
                                }}
                                data-testid="button-open-rating-modal"
                              >
                                Rate Now
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Thread Fully Closed Notice */}
                        {selectedThread?.threadPhase === 'closed' && (
                          <div className="mb-3 p-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg" data-testid="thread-completed-closed-notice">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                              <span className="text-sm text-green-800 dark:text-green-200">
                                This shipment is complete and the thread is closed.
                              </span>
                            </div>
                          </div>
                        )}

                        {isPartnerTyping && selectedThread?.threadPhase !== 'closed' && (
                          <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground" data-testid="typing-indicator">
                            <div className="flex space-x-1">
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-2 h-2 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </div>
                            <span>{selectedPartnerInfo?.firstName} is typing...</span>
                          </div>
                        )}
                        {pendingAttachment && selectedThread?.threadPhase !== 'closed' && (
                          <div className="flex items-center gap-2 mb-2 p-2 bg-muted rounded-md" data-testid="pending-attachment">
                            {pendingAttachment.type.startsWith('image/') ? (
                              <Image className="w-4 h-4 text-primary" />
                            ) : (
                              <FileText className="w-4 h-4 text-primary" />
                            )}
                            <span className="text-sm flex-1 truncate">{pendingAttachment.name}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => setPendingAttachment(null)}
                              data-testid="button-remove-attachment"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {selectedThread?.threadPhase !== 'closed' && (
                          <div className="flex gap-2">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              className="hidden"
                              accept="image/*,.pdf,.doc,.docx,.txt,.xls,.xlsx"
                              data-testid="input-file"
                            />
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isUploading}
                              title="Attach file"
                              data-testid="button-attach-file"
                            >
                              {isUploading ? (
                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Paperclip className="w-4 h-4" />
                              )}
                            </Button>
                            <Input 
                              placeholder="Type a message..."
                              value={newMessage}
                              onChange={(e) => {
                                setNewMessage(e.target.value);
                                handleTyping();
                              }}
                              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                              data-testid="input-message"
                            />
                            <Button 
                              size="icon" 
                              onClick={handleSend}
                              disabled={(!newMessage.trim() && !pendingAttachment) || sendMutation.isPending || isSending || isUploading}
                              data-testid="button-send"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            {canSendClaimInvitation && availableLoads.length > 0 && (
                              <Button 
                                size="icon" 
                                variant="outline"
                                onClick={() => setShowClaimInvitation(true)}
                                title="Send claim invitation"
                                data-testid="button-open-claim-invitation"
                              >
                                <Gift className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                        {selectedThread?.threadPhase !== 'closed' && canSendClaimInvitation && availableLoads.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Click the gift icon to offer one of your loads to this carrier.
                          </p>
                        )}
                    </>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-6">
                <div>
                  <MessageSquare className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-lg mb-2">Select a conversation</h3>
                  <p className="text-sm text-muted-foreground">
                    Choose a conversation from the list to view messages.
                  </p>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </Card>

      <LoadDetailModal
        load={loadModalData}
        open={loadModalOpen}
        onOpenChange={setLoadModalOpen}
        currentUserId={user?.id}
        currentUserType={user?.userType || undefined}
        currentUserVerified={user?.verificationStatus === "verified"}
        readOnly
      />

      {showRatingModal && selectedThread?.threadId && selectedThread?.loadId && (
        <RatingModal
          open={showRatingModal}
          onClose={() => {
            setShowRatingModal(false);
            setRatingModalAllowOverride(false);
          }}
          threadId={selectedThread.threadId}
          loadId={selectedThread.loadId}
          partnerId={selectedThread.partnerId}
          partnerName={`${selectedThread.partner.firstName} ${selectedThread.partner.lastName}`}
          partnerCompany={selectedThread.partner.company}
          partnerType={selectedThread.partner.userType}
          loadIdentifier={selectedThread.load?.loadIdentifier}
          origin={selectedThread.load?.origin}
          destination={selectedThread.load?.destination}
          completedAt={selectedThread.load?.completedAt}
          allowOverride={ratingModalAllowOverride}
        />
      )}
    </div>
  );
}
